import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CreerUtilisateurFictifModel, UtilisateurFictifModel } from 'src/models/pilotage/creerUtilisateurFictifModel';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { ResultCreerUtilisateursFictifsDialogComponent } from './result-creer-utilisateurs-fictifs-dialog/result-creer-utilisateurs-fictifs-dialog.component';
import { tap } from 'rxjs';
import * as XLSX from 'xlsx';

import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { ContexteUtilisateur, EtiquetteModel } from 'src/models/pilotage/contextePilotageUtilisateurModel';
import { EtiquetteService } from 'src/services/etiquette.service';


@Component({
    selector: 'app-creer-utilisateurs-fictifs-dialog',
    templateUrl: './creer-utilisateurs-fictifs-dialog.component.html',
    styleUrls: ['./creer-utilisateurs-fictifs-dialog.component.scss'],
    imports: [
    FormsModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    TranslateModule
]
})
export class CreerUtilisateursFictifsDialogComponent implements OnInit {
  selectedEntity: number = 0;
  entities: any[] = [];
  singleEntity: { id: number, name: string } | null = null;
  manualUser: { firstName: string, lastName: string } = { firstName: '', lastName: '' };
  selectedFile: File | null = null;
  creationMethod: 'excel' | 'manual' = 'manual';
  utilisateursExcel: UtilisateurFictifModel[] = [];
  isLoading: boolean = false;

  etiquettesDisponibles: EtiquetteModel[] = [];
  selectedEtiquettes: EtiquetteModel[] = [];
  availableEtiquettes: EtiquetteModel[] = [];

  groupeEtiquettes: EtiquetteModel[] = [];
  entiteEtiquettes: EtiquetteModel[] = [];

  constructor(
    private dialogRef: MatDialogRef<CreerUtilisateursFictifsDialogComponent>,
    private utilisateurService: UtilisateurService,
    public etiquetteService: EtiquetteService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loadEntities();
    this.loadEtiquettes();
  }

  loadEntities(): void {
    this.utilisateurService.obtenirContexte().subscribe(
      (context: ContexteUtilisateur) => {
        this.entities = context.entitesAccessibles
          .filter(re => !re.estGroupe)
          .map((e: any) => ({ id: e.entiteId, name: e.nom }));

        if (this.entities.length === 1) {
          this.singleEntity = this.entities[0];
          this.selectedEntity = this.singleEntity!.id;
        }
        this.loadEtiquettes();
      }
    );
  }

  loadEtiquettes(): void {
    const groupEntites = this.utilisateurService.contexteUtilisateur.toutesLesEntites
      .filter(e => e.estGroupe)
      .map(e => e.entiteId);

    this.groupeEtiquettes = this.utilisateurService.contexteUtilisateur.toutesLesEtiquettes
      .filter(e => e.entiteIds.some(id => groupEntites.includes(id)));

    this.updateAvailableEtiquettes();
  }

  updateAvailableEtiquettes(): void {
    const groupEntites = this.utilisateurService.contexteUtilisateur.toutesLesEntites
      .filter(e => e.estGroupe)
      .map(e => e.entiteId);

    const etiquettesGroupe = this.utilisateurService.contexteUtilisateur.toutesLesEtiquettes
      .filter(e => e.entiteIds.some(id => groupEntites.includes(id)));

    const etiquettesEntite = this.utilisateurService.contexteUtilisateur.toutesLesEtiquettes
      .filter(e => e.entiteIds.includes(this.selectedEntity));

    const nouvellesEtiquettesAccessibles = [...new Set([...etiquettesGroupe, ...etiquettesEntite])];

    this.selectedEtiquettes = this.selectedEtiquettes.filter(etiquette =>
      nouvellesEtiquettesAccessibles.some(etiquetteAccessible => etiquetteAccessible.etiquetteId === etiquette.etiquetteId)
    );

    this.availableEtiquettes = nouvellesEtiquettesAccessibles
      .filter(etiquette => !this.selectedEtiquettes.some(selected => selected.etiquetteId === etiquette.etiquetteId))
      .sort((a, b) =>
        this.etiquetteService.getNomLocalise(a.locales).localeCompare(
          this.etiquetteService.getNomLocalise(b.locales)
        )
      );
  }


  onEtiquetteSelected(event: Event, selectElement: HTMLSelectElement): void {
    const etiquetteId = Number(selectElement.value);
    const etiquetteSelectionnee = this.availableEtiquettes.find(e => e.etiquetteId === etiquetteId);
    if (etiquetteSelectionnee) {
      this.selectedEtiquettes.push(etiquetteSelectionnee);
      this.availableEtiquettes = this.availableEtiquettes.filter(e => e.etiquetteId !== etiquetteId);
      selectElement.value = '';
    }
  }

  removeSelectedEtiquette(id: number): void {
    const removedEtiquette = this.selectedEtiquettes.find(e => e.etiquetteId === id);
    this.selectedEtiquettes = this.selectedEtiquettes.filter(e => e.etiquetteId !== id);
    if (removedEtiquette) {
      this.availableEtiquettes.push(removedEtiquette);
      this.availableEtiquettes.sort((a, b) =>
        this.etiquetteService.getNomLocalise(a.locales).localeCompare(
          this.etiquetteService.getNomLocalise(b.locales)
        )
      );
    }
  }

  downloadExcelTemplate(): void {
    this.utilisateurService.obtenirModeleUtilisateurFictif().pipe(
      tap(
        (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'modele.xlsx';
          link.click();
          window.URL.revokeObjectURL(url);
        },
        error => {
          console.error('Error downloading file:', error);
        }
      )
    ).subscribe();
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    this.selectedFile = file;
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const excelData = XLSX.utils.sheet_to_json(worksheet);

      this.utilisateursExcel = excelData
        .map((row: any) => ({
          prenom: row.Prenom,
          nom: row.Nom
        }))
        .filter(user => user.prenom && user.nom);
    };

    reader.readAsBinaryString(file);
  }

  isCreateButtonDisabled(): boolean {
    if (this.selectedEntity === 0) {
      return true;
    }

    if (this.creationMethod === 'manual') {
      return this.manualUser.firstName.trim() === '' || this.manualUser.lastName.trim() === '';
    }

    if (this.creationMethod === 'excel') {
      return this.selectedFile === null || this.utilisateursExcel.length === 0;
    }
    return false;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    this.isLoading = true;
    const model: CreerUtilisateurFictifModel = {
      entiteId: this.selectedEntity,
      utilisateurs: this.creationMethod === 'manual'
        ? [{ prenom: this.manualUser.firstName, nom: this.manualUser.lastName }]
        : this.utilisateursExcel,
      etiquetteIds: this.selectedEtiquettes.map(e => e.etiquetteId)
    };

    this.utilisateurService.creerUtilisateursFictifs(model).subscribe({
      next: (result) => {
        this.isLoading = false;
        this.dialogRef.close(true);
        this.dialog.open(ResultCreerUtilisateursFictifsDialogComponent, {
          data: result,
          width: '900px',
          disableClose: true
        });
      },
      error: () => {
        this.isLoading = false;
        this.dialogRef.close(false);
      }
    });
  }

  onEntitySelected(event: any): void {
    this.selectedEntity = parseInt(event.target.value);
    this.updateAvailableEtiquettes();
  }


}
